import { z } from "zod";
import { SYSTEMS_PER_GALAXY, SYSTEM_POSITION_MAX } from "../constants/resources.js";
import { BATTLE_ROUND_OPTIONS, FLIGHT_SPEED_PERCENT_OPTIONS, SPACE_TRIP_MAX_HOURS, SPACE_TRIP_MAX_MINUTES, } from "../constants/fleets.js";
import { FLEET_SPEED_BOOST_MAX_PERCENT } from "../constants/premium.js";
/**
 * Request schemas for the fleet endpoints (`/sendFleet`, `/recallFleet`,
 * `/getFleets`). These validate SHAPE only — every gameplay rule (ship
 * availability, mission/target legality, flight limits, cargo capacity,
 * gas affordability) is enforced server-side against the replayed planet;
 * see §6.1/§25 of `docs/game-details/flight_management_system.md`:
 * client-computed distance/time/gas values are never accepted, the server
 * recomputes them all.
 */
export const FleetMissionTypeSchema = z.enum([
    "attack",
    "pirate",
    "transport",
    "station",
    "espionage",
    "colonize",
    "harvestAsteroid",
    "recycle",
    "spaceTrip",
    "sunDefence",
    "sunAttack",
    "sunGarrison",
]);
/**
 * Target coordinate — unlike `GalaxyPositionSchema`, `position: 0` (the
 * system's Sun) is representable here; whether a given mission may actually
 * target the Sun is a server-side rule (`FLEET_MISSION_DEFINITIONS`).
 */
export const FleetTargetSchema = z.object({
    galaxy: z.number().int().min(1).max(999),
    system: z.number().int().min(1).max(SYSTEMS_PER_GALAXY),
    position: z.number().int().min(0).max(SYSTEM_POSITION_MAX),
});
/** Ship manifest: `{ "3": 10 }` — keys are ShipIds 1-13 as strings (zod records key by string). */
export const FleetShipsSchema = z
    .record(z.string().regex(/^([1-9]|1[0-3])$/, "Ship id must be 1-13"), z.number().int().min(1))
    .refine((ships) => Object.keys(ships).length > 0, {
    message: "At least one ship must be selected",
});
export const FleetCargoSchema = z.object({
    metal: z.number().int().min(0).default(0),
    minerals: z.number().int().min(0).default(0),
    gas: z.number().int().min(0).default(0),
    scrap: z.number().int().min(0).default(0),
});
const speedValues = FLIGHT_SPEED_PERCENT_OPTIONS.map((v) => z.literal(v));
const battleRoundValues = BATTLE_ROUND_OPTIONS.map((v) => z.literal(v));
export const SendFleetSchema = z.object({
    originPlanetId: z.string().min(1),
    mission: FleetMissionTypeSchema,
    ships: FleetShipsSchema,
    /** Ignored for spaceTrip (fleet holds position); forced to the Sun for sunDefence. */
    target: FleetTargetSchema,
    speedPercent: z.union(speedValues).default(100),
    /** Dark Matter-bought travel-time reduction, 0-{@link FLEET_SPEED_BOOST_MAX_PERCENT} — 1 Dark Matter per percent. */
    speedBoostPercent: z.number().int().min(0).max(FLEET_SPEED_BOOST_MAX_PERCENT).default(0),
    cargo: FleetCargoSchema.default({ metal: 0, minerals: 0, gas: 0, scrap: 0 }),
    /**
     * Attack only (5 or 8, player-selectable). Pirate always fights a fixed
     * 3 rounds (`PIRATE_FIXED_BATTLE_ROUNDS`) and never accepts a
     * client-supplied value; every other mission is rejected server-side
     * (`fleetDispatch.ts`'s `allowsBattleRounds` check).
     */
    battleRounds: z.union(battleRoundValues).optional(),
    /** Space Trip only: manual one-way duration. */
    holdHours: z.number().int().min(0).max(SPACE_TRIP_MAX_HOURS).optional(),
    holdMinutes: z.number().int().min(0).max(SPACE_TRIP_MAX_MINUTES).optional(),
});
export const RecallFleetSchema = z.object({
    fleetId: z.string().min(1),
});
export const GetFleetsSchema = z.object({
    /** Optional origin filter — omitted returns every active fleet of the player. */
    originPlanetId: z.string().min(1).optional(),
});
export const ScanForSpiesSchema = z.object({
    planetId: z.string().min(1),
});
//# sourceMappingURL=fleet.schemas.js.map